import { PayloadAction, createSlice, current } from "@reduxjs/toolkit";
import { createNewEmployeeThunk, deleteEmployeeThunk, editEmployeeThunk, fetchEmployeeActionModulesThunk, fetchEmployeeByIdThunk, fetchEmployeeServiceModulesThunk, fetchEmployeeShopModulesThunk, patchEmployeeActionModulesThunk, patchEmployeeServiceModulesThunk, patchEmployeeShopModulesThunk, uploadEmployeesThunk } from "../thunk/employeesThunk";
import {
  SortingDataType,
  TableSortDirection,
  sortArrayBy,
} from "../../common/logic/TableSortUtils";
import { CreateEmployeeType, EmployeeGeneral, EmployeeUserType } from "../../types/employeeType";

interface EmployeeInitialState {
  newUserMode: boolean;
  users: EmployeeGeneral[] | [];
  companyEmployees: EmployeeGeneral[];
  userToEdit: EmployeeUserType | null;
  excelUploadEmployeesParsedData: CreateEmployeeType[] | null;
  employeeServiceModules: Record<string, number[]>;
  employeeActionModules: Record<string, number[]>;
  employeeShopModules: Record<string, number[]>;
}

const initialState: EmployeeInitialState = {
  newUserMode: false,
  users: [],
  companyEmployees: [],
  userToEdit: null,
  excelUploadEmployeesParsedData: [],
  employeeServiceModules: {},
  employeeActionModules: {},
  employeeShopModules: {},
};

const employeesReducer = createSlice({
  name: "employees",
  initialState,
  reducers: {
    sortEmployeesTable: (
      state,
      action: PayloadAction<{
        dataField: string;
        direction: TableSortDirection;
        dataType: SortingDataType;
      }>
    ) => {
      const { dataField, dataType, direction } = action.payload;
      const arrayToSort = current(state.users);
      state.users = sortArrayBy(arrayToSort, dataField, dataType, direction);
    },
    setUserToEdit: (
      state,
      action: PayloadAction<EmployeeUserType | null>
    ) => {
      state.userToEdit = action.payload;
    },
    setExcelUploadEmployeesParsedData: (state, action: PayloadAction<CreateEmployeeType[]>) => {
      state.excelUploadEmployeesParsedData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchEmployeeByIdThunk.fulfilled,
      (state, action) => {
        const payload = action.payload;
        if (!payload) return;
        const existingIndex = state.companyEmployees.findIndex(
          (emp) => emp.id === payload.id
        );
        if (existingIndex >= 0) {
          state.companyEmployees[existingIndex] = payload;
        } else {
          state.companyEmployees.push(payload);
        }
      }
    );
    builder.addCase(
      createNewEmployeeThunk.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.users = [...state.users, action.payload];
          state.newUserMode = false;
        }
        state.userToEdit = null;
      }
    );
    builder.addCase(
      deleteEmployeeThunk.fulfilled,
      (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
      }
    );
    builder.addCase(editEmployeeThunk.fulfilled, (state, action) => {
      const updatedUser = action.payload;
      state.users = state.users.map((user) =>
        user.id === updatedUser.id ? updatedUser : user
      );
      state.userToEdit = null;
    });
    builder.addCase(
      uploadEmployeesThunk.fulfilled,
      (state, action) => {
        state.companyEmployees = { ...state.companyEmployees, ...action.payload }
      }
    );
    builder.addCase(fetchEmployeeServiceModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeServiceModules[action.payload.employeeId] = action.payload.moduleIds;
    });
    builder.addCase(patchEmployeeServiceModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeServiceModules[action.payload.employeeId] = action.payload.moduleIds;
    });
    builder.addCase(fetchEmployeeActionModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeActionModules[action.payload.employeeId] = action.payload.moduleIds;
    });
    builder.addCase(patchEmployeeActionModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeActionModules[action.payload.employeeId] = action.payload.moduleIds;
    });
    builder.addCase(fetchEmployeeShopModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeShopModules[action.payload.employeeId] = action.payload.moduleIds;
    });
    builder.addCase(patchEmployeeShopModulesThunk.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.employeeShopModules[action.payload.employeeId] = action.payload.moduleIds;
    });
  },
});

export const {
  sortEmployeesTable,
  setUserToEdit,
  setExcelUploadEmployeesParsedData,
} = employeesReducer.actions;

export default employeesReducer.reducer;
